import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { getAdminToken } from "../components/Auth";
import { toast } from "sonner";
import {
  Bot, MessageSquare, DollarSign, RefreshCw, Send, FileText,
  Image as ImageIcon, QrCode, Bell, ChevronRight, Activity, Power,
  CheckCircle2, XCircle, Eye, Brain, Mic, Trash2, Sparkles, ChevronUp, ChevronDown,
  ShoppingBag, Plus, X, Edit2, BookOpen, UserPlus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CustomerCombobox from "../components/CustomerCombobox";

const Pill = ({ children, color = "gray" }) => (
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full bg-${color}-500/15 text-${color}-400`}>
    {children}
  </span>
);

const Card = ({ children, className = "", testid }) => (
  <div className={`bg-[#0F0F10] border border-[#27272A] rounded-2xl p-4 sm:p-5 ${className}`} data-testid={testid}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, color, title, badge, children }) => (
  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
    <div className="flex items-center gap-2 min-w-0">
      <div className={`p-1.5 rounded-lg bg-${color}-500/15 shrink-0`}>
        <Icon className={`text-${color}-400`} size={16} />
      </div>
      <h2 className="text-base font-bold text-white truncate">{title}</h2>
      {badge !== undefined && badge !== null && badge !== 0 && (
        <span className={`ml-1 px-2 py-0.5 rounded-full bg-${color}-500/20 text-${color}-400 text-xs font-bold shrink-0`}>
          {badge}
        </span>
      )}
    </div>
    <div className="flex items-center gap-1 shrink-0">{children}</div>
  </div>
);

const Toggle = ({ on, onClick, color = "green", testid }) => (
  <button
    onClick={onClick}
    className={`relative w-12 h-6 rounded-full transition-colors ${on ? `bg-${color}-500` : 'bg-[#27272A]'}`}
    data-testid={testid}
  >
    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${on ? 'left-7' : 'left-1'}`} />
  </button>
);

const WhatsAppBot = () => {
  const [botSettings, setBotSettings] = useState({});
  const [paymentClaims, setPaymentClaims] = useState([]);
  const [qrInfo, setQrInfo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedPayment, setSelectedPayment] = useState("");
  const [unpaidPayments, setUnpaidPayments] = useState([]);
  const [sendingStats, setSendingStats] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [prospectMsg, setProspectMsg] = useState("");
  const [prospectDays, setProspectDays] = useState(7);
  const [prospectCount, setProspectCount] = useState(null);
  const [prospectSending, setProspectSending] = useState(false);
  const [catalogCats, setCatalogCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Bot knowledge state
  const [knowledge, setKnowledge] = useState([]);
  const blankKnow = { title: "", content: "", active: true };
  const [newKnow, setNewKnow] = useState(blankKnow);
  const [editingKnowId, setEditingKnowId] = useState(null);
  const [editKnowDraft, setEditKnowDraft] = useState(blankKnow);

  // Sales catalog state
  const [salesProducts, setSalesProducts] = useState([]);
  const blankProduct = { name: "", price: "", power: "", hosting_fee: "", monthly_profit: "", warranty: "", notes: "", category: "", active: true };
  const [newProduct, setNewProduct] = useState(blankProduct);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProductDraft, setEditProductDraft] = useState(blankProduct);

  // AI Memory state
  const [memCustomerId, setMemCustomerId] = useState("");
  const [memData, setMemData] = useState(null);
  const [memEditSummary, setMemEditSummary] = useState("");
  const [memEditFacts, setMemEditFacts] = useState("");
  const [memSaving, setMemSaving] = useState(false);
  const [memRegenerating, setMemRegenerating] = useState(false);

  useEffect(() => {
    refreshAll();
    const t = setInterval(() => { fetchClaims(); fetchHistory(); }, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selectedCustomer) { setUnpaidPayments([]); return; }
    axios.get(`${API}/payments`)
      .then(r => setUnpaidPayments((r.data || []).filter(p => p.customer_id === selectedCustomer && p.status !== "paid")))
      .catch(() => setUnpaidPayments([]));
  }, [selectedCustomer]);

  const refreshAll = async () => {
    await Promise.all([fetchSettings(), fetchClaims(), fetchCustomers(), fetchHistory(), fetchSalesProducts(), fetchKnowledge(), fetchProspects(7), fetchCatalogCats()]);
  };

  const fetchSettings = async () => {
    try { const r = await axios.get(`${API}/bot-settings`); setBotSettings(r.data); } catch {}
  };
  const fetchClaims = async () => {
    try { const r = await axios.get(`${API}/payment-claims?status=pending`); setPaymentClaims(r.data || []); } catch {}
  };
  const fetchCustomers = async () => {
    try { const r = await axios.get(`${API}/customers`); setCustomers((r.data || []).filter(c => c.phone).sort((a,b) => (a.name||'').localeCompare(b.name||'', undefined, {sensitivity:'base'}))); } catch {}
  };
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/whatsapp/history/all?limit=50`);
      const data = r.data;
      const list = Array.isArray(data) ? data : (data.logs || data.history || data.data || []);
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setHistory(list);
    } catch {}
    setHistoryLoading(false);
  };

  const fetchKnowledge = async () => {
    try { const r = await axios.get(`${API}/bot-knowledge`); setKnowledge(r.data || []); } catch {}
  };

  const _knowPayload = (k) => ({
    title: (k.title || "").trim(),
    content: (k.content || "").trim(),
    active: k.active !== false,
  });

  const handleAddKnowledge = async () => {
    const payload = _knowPayload(newKnow);
    if (!payload.content) { toast.error("Write the rule or fact the bot should know"); return; }
    try {
      await axios.post(`${API}/bot-knowledge`, payload);
      toast.success("Added to bot knowledge");
      setNewKnow(blankKnow);
      fetchKnowledge();
    } catch { toast.error("Failed to add"); }
  };

  const startEditKnow = (k) => {
    setEditingKnowId(k.id);
    setEditKnowDraft({ title: k.title || "", content: k.content || "", active: k.active !== false });
  };

  const saveEditKnow = async () => {
    const payload = _knowPayload(editKnowDraft);
    if (!payload.content) { toast.error("Content is required"); return; }
    try {
      await axios.put(`${API}/bot-knowledge/${editingKnowId}`, payload);
      toast.success("Saved");
      setEditingKnowId(null);
      fetchKnowledge();
    } catch { toast.error("Failed to save"); }
  };

  const toggleKnowActive = async (k) => {
    try {
      await axios.put(`${API}/bot-knowledge/${k.id}`, _knowPayload({ ...k, active: !(k.active !== false) }));
      fetchKnowledge();
    } catch { toast.error("Failed to toggle"); }
  };

  const deleteKnow = async (k) => {
    if (!window.confirm(`Delete this rule? The bot will forget it.`)) return;
    try {
      await axios.delete(`${API}/bot-knowledge/${k.id}`);
      toast.success("Deleted");
      fetchKnowledge();
    } catch { toast.error("Failed to delete"); }
  };

  const fetchSalesProducts = async () => {
    try { const r = await axios.get(`${API}/sales-products`); setSalesProducts(r.data || []); } catch {}
  };

  const moveProduct = async (idx, dir) => {
    const next = [...salesProducts];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setSalesProducts(next);   // optimistic — reflect the new order immediately
    try {
      await axios.post(`${API}/sales-products/reorder`, { order: next.map(p => p.id) });
    } catch { toast.error("Could not save order"); fetchSalesProducts(); }
  };

  const _productPayload = (p) => ({
    name: (p.name || "").trim(),
    price: parseFloat(p.price) || 0,
    power: p.power || "",
    hosting_fee: parseFloat(p.hosting_fee) || 0,
    monthly_profit: p.monthly_profit || "",
    warranty: p.warranty || "",
    notes: p.notes || "",
    category: p.category || "",
    active: p.active !== false,
  });

  const handleAddProduct = async () => {
    const payload = _productPayload(newProduct);
    if (!payload.name) { toast.error("Enter a product name"); return; }
    try {
      await axios.post(`${API}/sales-products`, payload);
      toast.success(`Added ${payload.name}`);
      setNewProduct(blankProduct);
      fetchSalesProducts();
    } catch { toast.error("Failed to add product"); }
  };

  const startEditProduct = (p) => {
    setEditingProductId(p.id);
    setEditProductDraft({
      name: p.name || "", price: p.price || "", power: p.power || "",
      hosting_fee: p.hosting_fee || "", monthly_profit: p.monthly_profit || "",
      warranty: p.warranty || "", notes: p.notes || "", category: p.category || "", active: p.active !== false,
    });
  };

  const saveEditProduct = async () => {
    const payload = _productPayload(editProductDraft);
    if (!payload.name) { toast.error("Name is required"); return; }
    try {
      await axios.put(`${API}/sales-products/${editingProductId}`, payload);
      toast.success("Saved");
      setEditingProductId(null);
      fetchSalesProducts();
    } catch { toast.error("Failed to save"); }
  };

  const toggleProductActive = async (p) => {
    try {
      await axios.put(`${API}/sales-products/${p.id}`, _productPayload({ ...p, active: !(p.active !== false) }));
      fetchSalesProducts();
    } catch { toast.error("Failed to toggle"); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? The bot will no longer mention it.`)) return;
    try {
      await axios.delete(`${API}/sales-products/${p.id}`);
      toast.success("Deleted");
      fetchSalesProducts();
    } catch { toast.error("Failed to delete"); }
  };

  const updateSetting = async (key, value) => {
    try {
      await axios.put(`${API}/bot-settings`, { [key]: value });
      setBotSettings({ ...botSettings, [key]: value });
      const shown = typeof value === 'boolean' ? (value ? 'ON' : 'OFF') : value;
      toast.success(`${key.replace(/_/g, ' ')}: ${shown}`);
    } catch { toast.error("Failed to update"); }
  };

  const showQr = async () => {
    try { const r = await axios.get(`${API}/whatsapp/qr-code`); setQrInfo(r.data); }
    catch { toast.error("Failed to load QR"); }
  };

  const approveClaim = async (id) => {
    if (!window.confirm("Approve this payment claim? Customer's invoice will be marked PAID and they'll get a confirmation WhatsApp.")) return;
    try { await axios.post(`${API}/payment-claims/${id}/approve`); toast.success("Approved"); fetchClaims(); }
    catch { toast.error("Failed to approve"); }
  };
  const rejectClaim = async (id) => {
    const reason = window.prompt("Rejection reason (optional):", "Could not verify the payment.");
    if (reason === null) return;
    try { await axios.post(`${API}/payment-claims/${id}/reject`, { reason }); toast.success("Rejected"); fetchClaims(); }
    catch { toast.error("Failed to reject"); }
  };
  const dismissClaim = async (id) => {
    try { await axios.post(`${API}/payment-claims/${id}/dismiss`); toast.success("Dismissed — nothing sent to customer"); fetchClaims(); }
    catch { toast.error("Failed to dismiss"); }
  };

  const sendStats = async () => {
    if (!selectedCustomer) return toast.error("Pick a customer first");
    setSendingStats(true);
    try {
      const r = await axios.post(`${API}/whatsapp/send-stats-image`, { customer_id: selectedCustomer });
      r.data.success ? toast.success(`Stats sent to ${r.data.sent_to}`) : toast.error(r.data.error || "Failed");
    } catch (e) { toast.error("Send failed"); }
    setSendingStats(false);
  };
  const previewStats = async () => {
    if (!selectedCustomer) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to preview the stats image"); return; }
    try {
      const r = await axios.get(`${API}/customers/${selectedCustomer}/stats-preview.png`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      w.close();
      toast.error("Couldn't load the stats preview");
    }
  };
  const sendPdf = async () => {
    if (!selectedPayment) return toast.error("Pick an invoice first");
    setSendingPdf(true);
    try {
      const r = await axios.post(`${API}/whatsapp/send-invoice-pdf`, { payment_id: selectedPayment });
      r.data.success ? toast.success("PDF invoice sent!") : toast.error(r.data.error || "Failed");
    } catch (e) { toast.error("Send failed"); }
    setSendingPdf(false);
  };
  const previewPdf = () => {
    if (!selectedPayment) return;
    window.open(`${API}/payments/${selectedPayment}/invoice.pdf?admin_token=${encodeURIComponent(getAdminToken())}`, "_blank");
  };

  const sendBroadcast = async () => {
    if (!broadcastMsg.trim()) return toast.error("Empty message");
    if (!window.confirm(`Send this broadcast to ALL active customers?\n\n${broadcastMsg.slice(0, 200)}`)) return;
    setBroadcasting(true);
    try {
      const r = await axios.post(`${API}/whatsapp/broadcast`, { message: broadcastMsg });
      const sent = r.data?.sent || 0; const failed = r.data?.failed || 0;
      toast.success(`Broadcast complete · ${sent} sent · ${failed} failed`);
      setBroadcastMsg("");
      fetchHistory();
    } catch (e) { toast.error("Broadcast failed"); }
    setBroadcasting(false);
  };

  const fetchProspects = async (days) => {
    try {
      const r = await axios.get(`${API}/whatsapp/prospects`, { params: { days } });
      setProspectCount(r.data?.count ?? 0);
      // Only adopt the saved message the first time, so we don't clobber an edit.
      setProspectMsg((cur) => cur || r.data?.message || "");
    } catch { /* leave count null */ }
  };

  const fetchCatalogCats = async () => {
    try { const r = await axios.get(`${API}/catalog-categories`); setCatalogCats(r.data?.categories || []); } catch {}
  };
  const saveCats = async (next) => {
    setCatalogCats(next);
    try { await axios.put(`${API}/catalog-categories`, { categories: next }); }
    catch { toast.error("Could not save categories"); fetchCatalogCats(); }
  };
  const addCat = () => {
    const v = newCat.trim();
    if (!v || catalogCats.includes(v)) { setNewCat(""); return; }
    saveCats([...catalogCats, v]); setNewCat("");
  };
  const moveCat = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= catalogCats.length) return;
    const next = [...catalogCats]; [next[i], next[j]] = [next[j], next[i]]; saveCats(next);
  };
  const removeCat = (c) => saveCats(catalogCats.filter(x => x !== c));

  const saveProspectMsg = async () => {
    try {
      await axios.put(`${API}/whatsapp/prospect-message`, { message: prospectMsg });
      toast.success("Follow-up message saved");
    } catch { toast.error("Could not save"); }
  };

  const sendProspects = async () => {
    if (!prospectMsg.trim()) return toast.error("Empty message");
    if (!window.confirm(`Send this to ${prospectCount ?? "the"} non-customer(s) who messaged us in the last ${prospectDays} days?\n\n${prospectMsg.slice(0, 200)}`)) return;
    setProspectSending(true);
    try {
      await axios.put(`${API}/whatsapp/prospect-message`, { message: prospectMsg }).catch(() => {});
      const r = await axios.post(`${API}/whatsapp/prospects/broadcast`, { message: prospectMsg, days: prospectDays });
      const sent = r.data?.sent || 0; const failed = r.data?.failed || 0;
      toast.success(`Follow-up sent · ${sent} sent · ${failed} failed`);
      fetchHistory();
    } catch (e) { toast.error("Send failed"); }
    setProspectSending(false);
  };

  const fetchMemory = async (customerId) => {
    if (!customerId) { setMemData(null); setMemEditSummary(""); setMemEditFacts(""); return; }
    try {
      const r = await axios.get(`${API}/ai-memory/${customerId}`);
      setMemData(r.data);
      setMemEditSummary(r.data?.summary || "");
      setMemEditFacts((r.data?.facts || []).join("\n"));
    } catch { toast.error("Failed to load memory"); }
  };
  const saveMemory = async () => {
    if (!memCustomerId) return;
    setMemSaving(true);
    try {
      const facts = memEditFacts.split("\n").map(s => s.trim()).filter(Boolean);
      const r = await axios.put(`${API}/ai-memory/${memCustomerId}`, { summary: memEditSummary, facts });
      setMemData(r.data);
      toast.success("Memory saved");
    } catch { toast.error("Save failed"); }
    setMemSaving(false);
  };
  const regenerateMemory = async () => {
    if (!memCustomerId) return;
    setMemRegenerating(true);
    try {
      const r = await axios.post(`${API}/ai-memory/${memCustomerId}/regenerate`);
      setMemData(r.data);
      setMemEditSummary(r.data?.summary || "");
      setMemEditFacts((r.data?.facts || []).join("\n"));
      toast.success("Memory regenerated from recent messages");
    } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed"); }
    setMemRegenerating(false);
  };
  const clearMemory = async () => {
    if (!memCustomerId) return;
    if (!window.confirm("Wipe this customer's AI memory? The bot will start from scratch with them.")) return;
    try {
      await axios.delete(`${API}/ai-memory/${memCustomerId}`);
      setMemData({ customer_id: memCustomerId, summary: "", facts: [], message_count: 0 });
      setMemEditSummary("");
      setMemEditFacts("");
      toast.success("Memory cleared");
    } catch { toast.error("Clear failed"); }
  };
  useEffect(() => { fetchMemory(memCustomerId); }, [memCustomerId]);


  const runRemindersNow = async () => {
    toast.info("Sending reminders…");
    try {
      const r = await axios.post(`${API}/bot/run-reminder-check`);
      r.data.success ? toast.success(`Sent ${r.data.sent} reminders!`) : toast.error(r.data.message || r.data.error);
      fetchHistory();
    } catch { toast.error("Failed"); }
  };

  const sendUnpaidReminders = async () => {
    if (!window.confirm("Send a WhatsApp payment reminder to every customer who still owes this month's fee?\n\nCustomers who already paid, or who have prepaid months left, will NOT be messaged.")) return;
    toast.info("Sending reminders to unpaid customers…");
    try {
      const r = await axios.post(`${API}/bot/send-unpaid-reminders`);
      if (r.data.success) {
        if (r.data.sent === 0 && r.data.message) {
          toast.info(r.data.message);
        } else {
          toast.success(`Sent ${r.data.sent} reminders to unpaid customers${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
        }
      } else {
        toast.error(r.data.message || r.data.error || "Failed");
      }
      fetchHistory();
    } catch { toast.error("Failed"); }
  };

  const sendFinalReminders = async () => {
    if (!window.confirm("Send a STRONGER second reminder to every customer who still owes this month's fee?\n\nThis message warns that late payment can lead to the machine being switched offline. Customers who already paid, or who have prepaid months left, will NOT be messaged.")) return;
    toast.info("Sending second reminders to unpaid customers…");
    try {
      const r = await axios.post(`${API}/bot/send-final-reminders`);
      if (r.data.success) {
        if (r.data.sent === 0 && r.data.message) {
          toast.info(r.data.message);
        } else {
          toast.success(`Sent ${r.data.sent} second reminders to unpaid customers${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
        }
      } else {
        toast.error(r.data.message || r.data.error || "Failed");
      }
      fetchHistory();
    } catch { toast.error("Failed"); }
  };

  const runOfflineCheckNow = async () => {
    try {
      const r = await axios.post(`${API}/bot/run-offline-check`);
      toast.success(r.data.message || "Offline check ran");
    } catch { toast.error("Failed"); }
  };

  const sendTestPing = async () => {
    try {
      const r = await axios.post(`${API}/admin/notify-test`, { message: "🐯 Test ping from WhatsApp Bot console" });
      toast.success(`Ping sent to ${r.data.sent_to}`);
    } catch { toast.error("Failed"); }
  };

  const statusColor = (s) => {
    if (!s) return "gray";
    const v = String(s).toLowerCase();
    if (v.includes("fail")) return "red";
    if (v.includes("read")) return "green";
    if (v.includes("sent") || v.includes("delivered")) return "cyan";
    return "gray";
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="whatsapp-bot-page">
      {/* Header */}
      <div className="page-header">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <div className="p-2 rounded-xl bg-gradient-to-br from-[#25D366]/20 to-[#00E054]/10 border border-[#25D366]/30 shrink-0">
              <Bot className="text-[#25D366]" size={20} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">WhatsApp Bot Console</h1>
            <Pill color="green">Green API</Pill>
          </div>
          <p className="text-xs text-gray-500 sm:ml-12">Automation, payment claims, broadcasts, and customer messaging — all in one place.</p>
        </div>
        <Button variant="outline" size="sm" className="border-[#27272A] w-full sm:w-auto shrink-0" onClick={refreshAll}>
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Top row: Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card>
          <p className="text-xs text-gray-500 mb-1">Auto reminders</p>
          <p className={`text-lg font-bold ${botSettings.auto_reminders_enabled ? 'text-[#25D366]' : 'text-gray-500'}`}>
            {botSettings.auto_reminders_enabled ? "ENABLED" : "OFF"}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-gray-500 mb-1">Offline alerts</p>
          <p className={`text-lg font-bold ${botSettings.offline_alerts_enabled ? 'text-[#FFD700]' : 'text-gray-500'}`}>
            {botSettings.offline_alerts_enabled ? "ENABLED" : "OFF"}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-gray-500 mb-1">PDF auto-attach</p>
          <p className={`text-lg font-bold ${botSettings.attach_pdf_to_reminders ? 'text-[#06B6D4]' : 'text-gray-500'}`}>
            {botSettings.attach_pdf_to_reminders ? "ON" : "OFF"}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-gray-500 mb-1">AI Agent</p>
          <p className={`text-lg font-bold ${botSettings.ai_agent_enabled ? 'text-[#A855F7]' : 'text-gray-500'}`}>
            {botSettings.ai_agent_enabled ? "ON" : "OFF"}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-gray-500 mb-1">Pending claims</p>
          <p className={`text-lg font-bold ${paymentClaims.length > 0 ? 'text-[#FFD700]' : 'text-white'}`}>
            {paymentClaims.length}
          </p>
        </Card>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Bot Automation */}
        <Card testid="bot-automation">
          <SectionHeader icon={Bot} color="green" title="Automation" />
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gradient-to-br from-[#A855F7]/10 to-transparent rounded-xl border border-[#A855F7]/30">
              <div className="flex-1">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  🤖 AI Agent (Gemini)
                  <span className="text-[9px] bg-[#A855F7]/30 text-[#A855F7] px-1.5 py-0.5 rounded-full">beta</span>
                </p>
                <p className="text-xs text-gray-500">Wolf 🐺 personality · tools · memory · understands voice + images</p>
              </div>
              <Toggle
                on={!!botSettings.ai_agent_enabled}
                onClick={() => updateSetting("ai_agent_enabled", !botSettings.ai_agent_enabled)}
                color="purple"
                testid="toggle-ai-agent"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div className="flex-1">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  <ImageIcon size={14} className="text-pink-400" /> AI handles images & voice notes
                </p>
                <p className="text-xs text-gray-500">Photos → diagnoses · screenshots → explains · receipts → auto-claim · voice → transcribes</p>
              </div>
              <Toggle
                on={botSettings.ai_handle_media !== false}
                onClick={() => updateSetting("ai_handle_media", !(botSettings.ai_handle_media !== false))}
                color="purple"
                testid="toggle-ai-media"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div className="flex-1">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  <Mic size={14} className="text-cyan-400" /> AI voice replies
                </p>
                <p className="text-xs text-gray-500">When the customer sends a voice note, reply with a voice note (Gemini TTS)</p>
              </div>
              <Toggle
                on={!!botSettings.ai_voice_replies_enabled}
                onClick={() => updateSetting("ai_voice_replies_enabled", !botSettings.ai_voice_replies_enabled)}
                color="cyan"
                testid="toggle-ai-voice"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div className="flex-1">
                <p className="text-sm font-bold text-white">Pause bot when I reply by hand</p>
                <p className="text-xs text-gray-500">If you type to a customer yourself, the bot stays quiet in that one chat for the minutes below</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-gray-400">Quiet for</span>
                  <Input
                    type="number"
                    min={1}
                    className="h-7 w-20 bg-[#111] border-[#27272A] text-xs"
                    value={botSettings.human_takeover_minutes ?? 30}
                    onChange={(e) => setBotSettings({ ...botSettings, human_takeover_minutes: e.target.value })}
                    onBlur={(e) => {
                      const n = parseInt(e.target.value, 10);
                      updateSetting("human_takeover_minutes", Number.isFinite(n) && n > 0 ? n : 30);
                    }}
                    data-testid="input-takeover-minutes"
                  />
                  <span className="text-xs text-gray-400">minutes</span>
                </div>
              </div>
              <Toggle
                on={botSettings.human_takeover_enabled !== false}
                onClick={() => updateSetting("human_takeover_enabled", !(botSettings.human_takeover_enabled !== false))}
                color="green"
                testid="toggle-human-takeover"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div className="flex-1">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  <Sparkles size={14} className="text-emerald-400" /> Learn from my answers
                </p>
                <p className="text-xs text-gray-500">When you answer a customer by hand, the bot saves the useful info and uses it for other customers right away. See learned items in "Bot Knowledge &amp; Rules" below.</p>
              </div>
              <Toggle
                on={botSettings.learn_from_admin_enabled !== false}
                onClick={() => updateSetting("learn_from_admin_enabled", !(botSettings.learn_from_admin_enabled !== false))}
                color="green"
                testid="toggle-learn-from-admin"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div>
                <p className="text-sm font-bold text-white">Auto Payment Reminders</p>
                <p className="text-xs text-gray-500">Sends day 1 / 2 / 3 / every 3 days if unpaid</p>
              </div>
              <Toggle
                on={!!botSettings.auto_reminders_enabled}
                onClick={() => updateSetting("auto_reminders_enabled", !botSettings.auto_reminders_enabled)}
                color="green"
                testid="toggle-auto-reminders"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div>
                <p className="text-sm font-bold text-white">📄 Attach PDF invoice</p>
                <p className="text-xs text-gray-500">Sends a styled PDF with each reminder</p>
              </div>
              <Toggle
                on={!!botSettings.attach_pdf_to_reminders}
                onClick={() => updateSetting("attach_pdf_to_reminders", !botSettings.attach_pdf_to_reminders)}
                color="yellow"
                testid="toggle-attach-pdf"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-xl">
              <div>
                <p className="text-sm font-bold text-white">Offline Machine Alerts</p>
                <p className="text-xs text-gray-500">Pings admin when a miner drops</p>
              </div>
              <Toggle
                on={!!botSettings.offline_alerts_enabled}
                onClick={() => updateSetting("offline_alerts_enabled", !botSettings.offline_alerts_enabled)}
                color="red"
                testid="toggle-offline-alerts"
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button size="sm" variant="outline" className="border-[#27272A] text-xs" onClick={runRemindersNow}>
                <Send size={12} className="mr-1" /> Run reminders now
              </Button>
              <Button size="sm" className="bg-[#25D366] hover:bg-[#25D366]/80 text-black text-xs" onClick={sendUnpaidReminders} data-testid="btn-send-unpaid-reminders">
                <Send size={12} className="mr-1" /> Remind unpaid customers
              </Button>
              <Button size="sm" className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black text-xs" onClick={sendFinalReminders} data-testid="btn-send-final-reminders">
                <Send size={12} className="mr-1" /> Second reminder (don't be late)
              </Button>
              <Button size="sm" variant="outline" className="border-[#27272A] text-xs" onClick={runOfflineCheckNow}>
                <Activity size={12} className="mr-1" /> Check offline now
              </Button>
              <Button size="sm" variant="outline" className="border-[#27272A] text-xs" onClick={sendTestPing}>
                <Bell size={12} className="mr-1" /> Test admin ping
              </Button>
            </div>
          </div>
        </Card>

        {/* Payment Claims */}
        <Card testid="payment-claims">
          <SectionHeader icon={DollarSign} color="yellow" title="Payment Claims" badge={paymentClaims.length}>
            <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={fetchClaims}>
              <RefreshCw size={12} />
            </Button>
          </SectionHeader>
          <p className="text-xs text-gray-500 mb-3">When customers text <code className="bg-[#0A0A0A] px-1 rounded">paid</code> to the bot, they show up here for your approval.</p>
          {paymentClaims.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-6 text-center">No pending claims 🎉</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {paymentClaims.map(c => (
                <div key={c.id} className="bg-[#0A0A0A] rounded-xl p-3 border border-[#27272A]">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-bold text-white">{c.customer_name}</span>
                      <span className="text-xs text-gray-500 ml-2">{c.phone}</span>
                    </div>
                    <span className="text-[10px] text-gray-500">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                    <div><span className="text-gray-500">Month:</span> <span className="text-white">{c.month || "—"}</span></div>
                    <div><span className="text-gray-500">Expected:</span> <span className="text-[#FFD700] font-bold">${c.amount_expected ?? "—"}</span></div>
                  </div>
                  <p className="text-xs text-gray-400 italic mb-3 bg-[#1A1A1A] p-2 rounded">"{c.raw_message}"</p>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[#00E054] hover:bg-[#00E054]/80 text-black flex-1" onClick={() => approveClaim(c.id)}>
                      <CheckCircle2 size={14} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 flex-1" onClick={() => rejectClaim(c.id)}>
                      <XCircle size={14} className="mr-1" /> Reject
                    </Button>
                    <Button size="sm" variant="outline" title="Dismiss — nothing is sent to the customer" className="border-[#27272A] text-gray-400 hover:bg-white/5 px-3" onClick={() => dismissClaim(c.id)}>
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* AI Memory */}
      <Card testid="ai-memory">
        <SectionHeader icon={Brain} color="purple" title="AI Memory 🐺">
          <span className="text-[10px] text-gray-500 italic">What WKBeast Wolf remembers about each customer</span>
        </SectionHeader>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-2">
            <p className="text-xs text-gray-500">Customer</p>
            <CustomerCombobox
              customers={customers}
              value={memCustomerId}
              onChange={(id) => setMemCustomerId(id)}
              placeholder="— pick a customer —"
              accent="#A855F7"
              testid="ai-memory-customer-select"
            />
            {memData && (
              <div className="bg-[#0A0A0A] rounded-xl p-3 border border-[#27272A] text-xs space-y-1">
                <div><span className="text-gray-500">Phone:</span> <span className="text-white">{memData.phone || "—"}</span></div>
                <div><span className="text-gray-500">Messages exchanged:</span> <span className="text-[#A855F7] font-bold">{memData.message_count ?? 0}</span></div>
                <div><span className="text-gray-500">Last updated:</span> <span className="text-white">{memData.updated_at ? new Date(memData.updated_at).toLocaleString() : "never"}</span></div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" className="border-[#A855F7]/40 text-[#A855F7] text-xs" disabled={!memCustomerId || memRegenerating} onClick={regenerateMemory} data-testid="btn-regenerate-memory">
                <Sparkles size={12} className="mr-1" /> {memRegenerating ? "Working…" : "Regenerate"}
              </Button>
              <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 text-xs" disabled={!memCustomerId} onClick={clearMemory} data-testid="btn-clear-memory">
                <Trash2 size={12} className="mr-1" /> Wipe
              </Button>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Rolling summary (auto-updates every 10 messages)</p>
              <textarea
                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#A855F7] min-h-[90px] disabled:opacity-50"
                placeholder={memCustomerId ? "No summary yet — chat a few times or click Regenerate." : "Pick a customer to view their memory."}
                value={memEditSummary}
                onChange={(e) => setMemEditSummary(e.target.value)}
                disabled={!memCustomerId}
                data-testid="ai-memory-summary"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Facts (one per line)</p>
              <textarea
                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#A855F7] min-h-[120px] font-mono disabled:opacity-50"
                placeholder={"prefers Arabic\nowns 6 L7 miners\nasked for repair invoice last month"}
                value={memEditFacts}
                onChange={(e) => setMemEditFacts(e.target.value)}
                disabled={!memCustomerId}
                data-testid="ai-memory-facts"
              />
            </div>
            <div className="flex justify-end">
              <Button size="sm" className="bg-[#A855F7] hover:bg-[#A855F7]/80 text-white" disabled={!memCustomerId || memSaving} onClick={saveMemory} data-testid="btn-save-memory">
                {memSaving ? "Saving…" : "💾 Save Memory"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Send to Customer */}
      <Card testid="send-to-customer">
        <SectionHeader icon={Send} color="cyan" title="Send to Customer" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Customer</p>
              <CustomerCombobox
                customers={customers}
                value={selectedCustomer}
                onChange={(id) => { setSelectedCustomer(id); setSelectedPayment(""); }}
                placeholder="— pick a customer —"
                accent="#06B6D4"
              />
            </div>
            <div className="bg-[#0A0A0A] rounded-xl p-3 border border-[#27272A]">
              <p className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
                <ImageIcon size={12} className="text-cyan-400" /> Live stats screenshot
              </p>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[#06B6D4] hover:bg-[#06B6D4]/80 text-black flex-1" disabled={!selectedCustomer || sendingStats} onClick={sendStats}>
                  {sendingStats ? "Sending…" : "📊 Send Stats"}
                </Button>
                <Button size="sm" variant="outline" className="border-[#27272A] text-xs" disabled={!selectedCustomer} onClick={previewStats}>
                  <Eye size={12} />
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Unpaid invoice</p>
              <select
                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#FFD700]"
                value={selectedPayment}
                onChange={(e) => setSelectedPayment(e.target.value)}
                disabled={!selectedCustomer}
              >
                <option value="">{selectedCustomer ? "— pick an invoice —" : "Pick a customer first"}</option>
                {unpaidPayments.map(p => <option key={p.id} value={p.id}>{p.month} — ${p.amount}</option>)}
              </select>
            </div>
            <div className="bg-[#0A0A0A] rounded-xl p-3 border border-[#27272A]">
              <p className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
                <FileText size={12} className="text-yellow-400" /> PDF invoice
              </p>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black flex-1" disabled={!selectedPayment || sendingPdf} onClick={sendPdf}>
                  {sendingPdf ? "Sending…" : "📄 Send PDF"}
                </Button>
                <Button size="sm" variant="outline" className="border-[#27272A] text-xs" disabled={!selectedPayment} onClick={previewPdf}>
                  <Eye size={12} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* QR + Broadcast */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card testid="qr-section">
          <SectionHeader icon={QrCode} color="green" title="Bot QR Code">
            <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={showQr}>
              {qrInfo ? "Refresh" : "Generate"}
            </Button>
          </SectionHeader>
          <p className="text-xs text-gray-500 mb-3">Print or share this. Customers scan → opens WhatsApp chat with the bot pre-filled with "Hi" → bot replies with welcome menu.</p>
          {qrInfo ? (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <img src={qrInfo.qr_image_url} alt="WhatsApp Bot QR" className="w-40 h-40 bg-white p-2 rounded-xl" />
              <div className="flex-1 text-sm space-y-2 w-full">
                <div><span className="text-gray-500 text-xs">Bot phone:</span> <span className="font-mono text-white">{qrInfo.bot_phone}</span></div>
                <div className="break-all"><span className="text-gray-500 text-xs">Direct link:</span><br /><a href={qrInfo.wa_link} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline text-xs">{qrInfo.wa_link}</a></div>
                <Button size="sm" variant="outline" className="border-[#27272A] text-xs" onClick={() => { navigator.clipboard.writeText(qrInfo.wa_link); toast.success("Copied!"); }}>
                  Copy link
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic py-6 text-center">Click "Generate" to view the QR.</p>
          )}
        </Card>

        <Card testid="broadcast-section">
          <SectionHeader icon={MessageSquare} color="green" title="Broadcast" />
          <p className="text-xs text-gray-500 mb-3">Send a message to ALL active customers with WhatsApp enabled.</p>
          <textarea
            value={broadcastMsg}
            onChange={(e) => setBroadcastMsg(e.target.value)}
            placeholder="Type your broadcast…"
            className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#25D366] min-h-[140px]"
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-gray-500">{broadcastMsg.length} chars</span>
            <Button size="sm" className="bg-[#25D366] hover:bg-[#25D366]/80 text-black" disabled={!broadcastMsg.trim() || broadcasting} onClick={sendBroadcast}>
              {broadcasting ? "Sending…" : (<><Send size={14} className="mr-1" /> Send to all</>)}
            </Button>
          </div>
        </Card>

        <Card testid="prospect-followup-section">
          <SectionHeader icon={UserPlus} color="cyan" title="Follow up with prospects" />
          <p className="text-xs text-gray-500 mb-3">
            Message people who texted us recently but are <b>not customers</b> — the ones who ask and
            disappear. Nudges them back and points them at the latest offers.
          </p>
          <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
            <span>Reach people from the last</span>
            <select
              value={prospectDays}
              onChange={(e) => { const d = Number(e.target.value); setProspectDays(d); fetchProspects(d); }}
              className="bg-[#0A0A0A] border border-[#27272A] rounded px-2 py-1 text-white outline-none"
              data-testid="prospect-days"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
            <span className="text-[#00C2FF] font-medium">
              {prospectCount === null ? "…" : `${prospectCount} prospect(s)`}
            </span>
          </div>
          <textarea
            value={prospectMsg}
            onChange={(e) => setProspectMsg(e.target.value)}
            placeholder="Message to send to prospects…"
            className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00C2FF] min-h-[120px]"
            data-testid="prospect-message"
          />
          <div className="flex justify-between items-center mt-3">
            <button className="text-xs text-gray-400 hover:text-white underline" onClick={saveProspectMsg}>
              Save message
            </button>
            <Button size="sm" className="bg-[#00C2FF] hover:bg-[#00C2FF]/80 text-black font-bold"
                    disabled={!prospectMsg.trim() || prospectSending || !prospectCount}
                    onClick={sendProspects} data-testid="send-prospects">
              {prospectSending ? "Sending…" : (<><Send size={14} className="mr-1" /> Send to {prospectCount ?? 0} prospect(s)</>)}
            </Button>
          </div>
        </Card>
      </div>

      {/* Bot Knowledge (custom rules + facts injected into the bot's brain) */}
      <Card testid="bot-knowledge">
        <SectionHeader icon={BookOpen} color="purple" title="Bot Knowledge & Rules" badge={knowledge.length}>
          <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={fetchKnowledge}>
            <RefreshCw size={12} />
          </Button>
        </SectionHeader>
        <p className="text-xs text-gray-500 mb-3">
          Add rules, facts, policies — anything you want the bot to know or do. Each item gets injected into the bot's instructions. Examples: "Never offer payment plans", "Our farm is in Dubai Industrial City", "If asked about repairs, say turnaround is 3-5 days", "Speak formal Arabic with older customers".
        </p>

        {knowledge.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">No custom rules yet. Add your first one below — the bot will use it on the next message.</p>
        ) : (
          <div className="space-y-2 mb-4">
            {knowledge.map(k => (
              <div key={k.id} className={`bg-[#0A0A0A] rounded-lg border ${k.active !== false ? 'border-[#27272A]' : 'border-red-500/30 opacity-60'} p-3`}>
                {editingKnowId === k.id ? (
                  <div className="space-y-2">
                    <input
                      className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white"
                      placeholder="Short title (optional, e.g. 'Payment policy')"
                      value={editKnowDraft.title}
                      onChange={e => setEditKnowDraft({ ...editKnowDraft, title: e.target.value })}
                    />
                    <textarea
                      className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white min-h-[80px]"
                      placeholder="The rule or fact the bot must follow..."
                      value={editKnowDraft.content}
                      onChange={e => setEditKnowDraft({ ...editKnowDraft, content: e.target.value })}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="border-[#27272A]" onClick={() => setEditingKnowId(null)}>Cancel</Button>
                      <Button size="sm" className="bg-[#A855F7] text-white" onClick={saveEditKnow}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      {k.title ? <p className="text-white font-bold text-sm mb-1">{k.title}</p> : null}
                      <p className="text-xs text-gray-300 whitespace-pre-wrap">{k.content}</p>
                      <div className="flex gap-1 mt-1">
                        {k.source === "learned" ? <Pill color="emerald">auto-learned</Pill> : null}
                        {k.active === false ? <Pill color="red">disabled</Pill> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Toggle on={k.active !== false} onClick={() => toggleKnowActive(k)} color="purple" />
                      <Button size="sm" variant="outline" className="border-[#27272A]" onClick={() => startEditKnow(k)} title="Edit">
                        <Edit2 size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#27272A] text-red-400 hover:bg-red-500/10" onClick={() => deleteKnow(k)} title="Delete">
                        <X size={12} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add new knowledge */}
        <div className="p-3 bg-[#0A0A0A] rounded-lg border border-dashed border-[#27272A]">
          <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
            <Plus size={12} /> Teach the bot something new
          </p>
          <input
            className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white mb-2"
            placeholder="Short title (optional, e.g. 'Payment policy', 'Farm location', 'Repair turnaround')"
            value={newKnow.title}
            onChange={e => setNewKnow({ ...newKnow, title: e.target.value })}
          />
          <textarea
            className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white min-h-[80px] mb-2"
            placeholder="Write the rule or fact. Plain language. Example: 'All machine purchases are paid in full up front. Delivery is 2 weeks. Never offer payment plans.'"
            value={newKnow.content}
            onChange={e => setNewKnow({ ...newKnow, content: e.target.value })}
          />
          <div className="flex justify-end">
            <Button onClick={handleAddKnowledge} className="bg-[#A855F7] text-white">
              <Plus size={14} className="mr-1" /> Add to bot brain
            </Button>
          </div>
        </div>
      </Card>

      {/* Sales Catalog (drives the bot's answers about products) */}
      <Card testid="sales-catalog">
        <SectionHeader icon={ShoppingBag} color="amber" title="Sales Catalog" badge={salesProducts.length}>
          <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={fetchSalesProducts}>
            <RefreshCw size={12} />
          </Button>
        </SectionHeader>
        <p className="text-xs text-gray-500 mb-3">
          Items you sell. The bot reads this list and answers customer questions about price, power, profit and warranty using it.
        </p>

        {/* Category manager: names + order shown on the public catalog page */}
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 mb-4">
          <p className="text-xs text-gray-400 mb-2">
            Categories on the public catalog page (wkbeast.org/catalog) — drag order with the arrows. Assign each machine to one when you edit it.
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {catalogCats.map((c, i) => (
              <div key={c} className="flex items-center gap-1 bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1">
                <button className="text-gray-500 hover:text-white disabled:opacity-20" disabled={i === 0} onClick={() => moveCat(i, -1)} title="Left">‹</button>
                <span className="text-xs text-white">{c}</span>
                <button className="text-gray-500 hover:text-white disabled:opacity-20" disabled={i === catalogCats.length - 1} onClick={() => moveCat(i, 1)} title="Right">›</button>
                <button className="text-red-400 hover:text-red-300 ml-1" onClick={() => removeCat(c)} title="Remove">×</button>
              </div>
            ))}
            {catalogCats.length === 0 && <span className="text-xs text-gray-600">No categories yet — add one.</span>}
          </div>
          <div className="flex gap-2">
            <input className="flex-1 bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white"
                   placeholder="Add a category, e.g. Bitcoin (BTC)" value={newCat}
                   onChange={e => setNewCat(e.target.value)}
                   onKeyDown={e => { if (e.key === "Enter") addCat(); }}
                   data-testid="add-category-input" />
            <Button size="sm" className="bg-[#F59E0B] text-black" onClick={addCat}>Add</Button>
          </div>
        </div>

        {salesProducts.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">No products yet. Add your first one below.</p>
        ) : (
          <div className="space-y-2 mb-4">
            {salesProducts.map((p, idx) => (
              <div key={p.id} className={`bg-[#0A0A0A] rounded-lg border ${p.active !== false ? 'border-[#27272A]' : 'border-red-500/30 opacity-60'} p-3`}>
                {editingProductId === p.id ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Name" value={editProductDraft.name} onChange={e => setEditProductDraft({ ...editProductDraft, name: e.target.value })} />
                      <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Price ($)" type="number" value={editProductDraft.price} onChange={e => setEditProductDraft({ ...editProductDraft, price: e.target.value })} />
                      <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Power (e.g. 16 Amps / 3200W)" value={editProductDraft.power} onChange={e => setEditProductDraft({ ...editProductDraft, power: e.target.value })} />
                      <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Hosting fee ($/mo)" type="number" value={editProductDraft.hosting_fee} onChange={e => setEditProductDraft({ ...editProductDraft, hosting_fee: e.target.value })} />
                      <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Monthly profit (e.g. $500-$1500)" value={editProductDraft.monthly_profit} onChange={e => setEditProductDraft({ ...editProductDraft, monthly_profit: e.target.value })} />
                      <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Warranty (e.g. 12 months)" value={editProductDraft.warranty} onChange={e => setEditProductDraft({ ...editProductDraft, warranty: e.target.value })} />
                    </div>
                    <input className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Notes (promo, coin, anything extra)" value={editProductDraft.notes} onChange={e => setEditProductDraft({ ...editProductDraft, notes: e.target.value })} />
                    <select className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" value={editProductDraft.category} onChange={e => setEditProductDraft({ ...editProductDraft, category: e.target.value })}>
                      <option value="">Category — auto-detect by coin</option>
                      {catalogCats.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="border-[#27272A]" onClick={() => setEditingProductId(null)}>Cancel</Button>
                      <Button size="sm" className="bg-[#F59E0B] text-black" onClick={saveEditProduct}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-white font-bold">{p.name}</p>
                        {p.price ? <Pill color="green">${Number(p.price).toLocaleString()}</Pill> : null}
                        {p.power ? <Pill color="cyan">{p.power}</Pill> : null}
                        {p.hosting_fee ? <Pill color="amber">${p.hosting_fee}/mo hosting</Pill> : null}
                        {p.warranty ? <Pill color="purple">{p.warranty} warranty</Pill> : null}
                        {p.active === false ? <Pill color="red">hidden from bot</Pill> : null}
                      </div>
                      {p.monthly_profit ? <p className="text-xs text-gray-400">Profit: {p.monthly_profit}</p> : null}
                      {p.notes ? <p className="text-xs text-gray-500 mt-1">{p.notes}</p> : null}
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="flex flex-col mr-1">
                        <button className="text-gray-500 hover:text-white disabled:opacity-20"
                                disabled={idx === 0} onClick={() => moveProduct(idx, -1)}
                                title="Move up" data-testid={`product-up-${p.id}`}>
                          <ChevronUp size={14} />
                        </button>
                        <button className="text-gray-500 hover:text-white disabled:opacity-20"
                                disabled={idx === salesProducts.length - 1} onClick={() => moveProduct(idx, 1)}
                                title="Move down" data-testid={`product-down-${p.id}`}>
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      <Toggle on={p.active !== false} onClick={() => toggleProductActive(p)} color="amber" />
                      <Button size="sm" variant="outline" className="border-[#27272A]" onClick={() => startEditProduct(p)} title="Edit">
                        <Edit2 size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#27272A] text-red-400 hover:bg-red-500/10" onClick={() => deleteProduct(p)} title="Delete">
                        <X size={12} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add new product */}
        <div className="p-3 bg-[#0A0A0A] rounded-lg border border-dashed border-[#27272A]">
          <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
            <Plus size={12} /> Add a product the bot can sell
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Name (e.g. Antminer L7)" value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} />
            <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Price ($)" type="number" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} />
            <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Power (e.g. 16 Amps / 3200W)" value={newProduct.power} onChange={e => setNewProduct({ ...newProduct, power: e.target.value })} />
            <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Hosting fee ($/mo)" type="number" value={newProduct.hosting_fee} onChange={e => setNewProduct({ ...newProduct, hosting_fee: e.target.value })} />
            <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Monthly profit (e.g. $500-$1500)" value={newProduct.monthly_profit} onChange={e => setNewProduct({ ...newProduct, monthly_profit: e.target.value })} />
            <input className="bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white" placeholder="Warranty (e.g. 12 months)" value={newProduct.warranty} onChange={e => setNewProduct({ ...newProduct, warranty: e.target.value })} />
          </div>
          <input className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white mb-2" placeholder="Notes (promo, coin mined, anything extra)" value={newProduct.notes} onChange={e => setNewProduct({ ...newProduct, notes: e.target.value })} />
          <select className="w-full bg-[#1A1A1A] border border-[#27272A] rounded px-2 py-1.5 text-sm text-white mb-2" value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}>
            <option value="">Category — auto-detect by coin</option>
            {catalogCats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex justify-end">
            <Button onClick={handleAddProduct} className="bg-[#F59E0B] text-black">
              <Plus size={14} className="mr-1" /> Add to catalog
            </Button>
          </div>
        </div>
      </Card>

      {/* Recent Activity */}
      <Card testid="recent-activity">
        <SectionHeader icon={Activity} color="purple" title="Recent Activity">
          <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={fetchHistory}>
            <RefreshCw size={12} className={historyLoading ? "animate-spin" : ""} />
          </Button>
        </SectionHeader>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-6 text-center">No messages yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {history.slice(0, 50).map((h, i) => {
              const inbound = h.direction === "inbound";
              const c = statusColor(h.status);
              return (
                <div key={h.id || i} className="bg-[#0A0A0A] rounded-lg px-3 py-2 border border-[#27272A]/70 flex items-start gap-2 text-xs">
                  <div className={`mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold ${inbound ? 'bg-cyan-500/15 text-cyan-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                    {inbound ? "IN" : "OUT"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-white font-semibold truncate">{h.customer_name || h.phone}</span>
                      <span className="text-[10px] text-gray-500 truncate">{h.phone}</span>
                      <Pill color={c}>{(h.delivery_status || h.status || "").toString().slice(0, 18)}</Pill>
                      <span className="text-[10px] text-gray-600 ml-auto whitespace-nowrap">{(h.created_at || "").slice(11, 19)}</span>
                    </div>
                    <p className="text-gray-400 line-clamp-2">{h.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default WhatsAppBot;
